import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

function isDarkColor(color) {
  if (!color) return false;
  const normalized = String(color).toLowerCase().replace(/\s+/g, "");
  return [
    "#000",
    "#000000",
    "#050505",
    "#09090b",
    "#18181b",
    "black",
    "rgb(0,0,0)",
    "rgb(5,5,5)",
    "rgb(9,9,11)",
    "rgb(24,24,27)",
    "rgba(0,0,0,1)",
  ].includes(normalized);
}

function normalizeSurfaceColor(color, isLight, fallback = "transparent") {
  if (!isLight) return color || fallback;
  const normalized = String(color || "").toLowerCase().replace(/\s+/g, "");
  if (
    !color ||
    isDarkColor(color) ||
    ["#fccf1f", "#e6bc1c", "rgb(252,207,31)", "rgb(230,188,28)"].includes(normalized)
  ) {
    return fallback;
  }
  return color;
}

function normalizeOverlayColor(color, isLight) {
  if (!color) return isLight ? "rgba(110,70,214,0.08)" : "rgba(0,0,0,0.5)";
  if (!isLight) return color;
  return isDarkColor(color) ? "rgba(110,70,214,0.12)" : color;
}

function useThemeAwareBackground(style, fallback = "transparent") {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  return {
    isLight,
    sectionStyle: {
      ...style,
      backgroundColor: normalizeSurfaceColor(style?.backgroundColor, isLight, fallback),
    },
  };
}

function getHeroVariant(pageSlug) {
  if (pageSlug === "home") return "bar-game-night";
  if (pageSlug === "contratar") return "bar-game-night";
  if (pageSlug === "sobre") return "bar-game-night";
  return "default";
}

function getHeroPalette(variant, isLight) {
  const fallback = {
    base: null,
    title: isLight ? "#21123f" : "#fccf1f",
    subtitle: isLight ? "rgba(34,24,68,0.82)" : "rgba(255,255,255,0.82)",
    description: isLight ? "rgba(47,37,86,0.68)" : "rgba(255,255,255,0.64)",
    overlay: isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.5)",
    primaryButton: isLight
      ? "bg-[#6e46d6] text-white hover:bg-[#5d38c6] font-bold px-8 py-6 text-lg shadow-[0_12px_30px_rgba(110,70,214,0.20)]"
      : "bg-[#fccf1f] text-black hover:bg-[#fccf1f]/90 font-bold px-8 py-6 text-lg",
    outlineButton: isLight
      ? "border-2 border-[#6e46d6]/30 text-[#6e46d6] bg-white/70 hover:bg-[#6e46d6] hover:text-white font-bold px-8 py-6 text-lg"
      : "border-2 border-[#fccf1f] text-[#fccf1f] bg-transparent hover:bg-[#fccf1f] hover:text-black font-bold px-8 py-6 text-lg",
    ghostButton: isLight
      ? "bg-white/70 text-[#2b185e] hover:bg-white font-bold px-8 py-6 text-lg"
      : "bg-white/10 text-white hover:bg-white/20 font-bold px-8 py-6 text-lg",
  };

  const variants = {
    "premium-show": {
      ...fallback,
      base: isLight
        ? "linear-gradient(180deg, rgba(255,253,248,0.98) 0%, rgba(245,240,255,0.98) 100%)"
        : "linear-gradient(180deg, rgba(10,8,17,0.98) 0%, rgba(18,12,33,0.98) 100%)",
      overlay: isLight ? "rgba(255,255,255,0.34)" : "rgba(5,5,5,0.32)",
    },
    "bar-game-night": {
      ...fallback,
      base: isLight
        ? "radial-gradient(circle at top left, rgba(255,214,102,0.22) 0%, rgba(255,253,248,0.98) 34%, rgba(243,236,255,0.98) 100%)"
        : "linear-gradient(180deg, rgba(24,10,14,0.98) 0%, rgba(46,18,35,0.98) 100%)",
      title: isLight ? "#43235f" : "#fccf1f",
      subtitle: isLight ? "rgba(60,33,90,0.82)" : fallback.subtitle,
      description: isLight ? "rgba(84,57,113,0.72)" : fallback.description,
      overlay: isLight ? "rgba(255,250,242,0.26)" : "rgba(18,8,12,0.34)",
      outlineButton: isLight
        ? "border-2 border-[#b77900]/25 text-[#946200] bg-white/65 hover:bg-[#b77900] hover:text-white font-bold px-8 py-6 text-lg"
        : fallback.outlineButton,
    },
    "futuristic-playful": {
      ...fallback,
      base: isLight
        ? "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,245,255,0.98) 45%, rgba(245,240,255,0.98) 100%)"
        : "linear-gradient(180deg, rgba(7,10,20,0.98) 0%, rgba(12,15,32,0.98) 100%)",
      title: isLight ? "#1d2354" : "#fccf1f",
      subtitle: isLight ? "rgba(33,41,95,0.80)" : fallback.subtitle,
      description: isLight ? "rgba(55,64,123,0.70)" : fallback.description,
      overlay: isLight ? "rgba(255,255,255,0.14)" : "rgba(8,10,18,0.22)",
      outlineButton: isLight
        ? "border-2 border-[#5f63ff]/24 text-[#4a50d8] bg-white/65 hover:bg-[#5f63ff] hover:text-white font-bold px-8 py-6 text-lg"
        : fallback.outlineButton,
    },
  };

  return variants[variant] || fallback;
}

function HeroArtwork({ variant, isLight }) {
  if (variant === "premium-show") {
    return (
      <>
        <div className="absolute inset-x-0 top-0 h-44" style={{ background: isLight ? "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.40) 100%)" : "linear-gradient(180deg, rgba(5,5,5,0.88) 0%, rgba(5,5,5,0.10) 100%)" }} />
        <div className="absolute -top-24 left-1/2 h-[24rem] w-[24rem] -translate-x-1/2 rounded-full blur-3xl" style={{ background: isLight ? "rgba(110,70,214,0.14)" : "rgba(110,70,214,0.22)" }} />
        <div className="absolute right-[8%] top-[22%] h-48 w-48 rounded-full border" style={{ borderColor: isLight ? "rgba(183,121,0,0.22)" : "rgba(252,207,31,0.24)" }} />
        <div className="absolute left-[10%] top-[28%] h-32 w-32 rounded-full border-2" style={{ borderColor: isLight ? "rgba(110,70,214,0.16)" : "rgba(110,70,214,0.28)" }} />
        <div className="absolute left-[12%] bottom-[18%] h-20 w-48 rounded-full border" style={{ borderColor: isLight ? "rgba(110,70,214,0.18)" : "rgba(252,207,31,0.20)", transform: "rotate(-14deg)" }} />
        <div className="absolute right-[12%] bottom-[16%] h-20 w-56 rounded-full border" style={{ borderColor: isLight ? "rgba(183,121,0,0.18)" : "rgba(255,255,255,0.14)", transform: "rotate(14deg)" }} />
        <span
          className="absolute right-[14%] top-[18%] font-nunito font-black text-7xl leading-none"
          style={{ color: isLight ? "rgba(110,70,214,0.18)" : "rgba(252,207,31,0.22)" }}
        >
          ?
        </span>
        <span
          className="absolute left-[16%] bottom-[16%] font-nunito font-black text-6xl leading-none"
          style={{ color: isLight ? "rgba(183,121,0,0.16)" : "rgba(255,255,255,0.16)" }}
        >
          !
        </span>
      </>
    );
  }

  if (variant === "bar-game-night") {
    return (
      <>
        <div className="absolute inset-x-0 top-0 h-44" style={{ background: isLight ? "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,249,239,0.40) 100%)" : "linear-gradient(180deg, rgba(15,8,12,0.90) 0%, rgba(15,8,12,0.16) 100%)" }} />
        <div className="absolute left-[8%] top-[20%] h-40 w-40 rounded-[2rem] blur-2xl" style={{ background: isLight ? "rgba(183,121,0,0.14)" : "rgba(252,207,31,0.16)", transform: "rotate(12deg)" }} />
        <div className="absolute right-[12%] top-[24%] h-52 w-52 rounded-full blur-3xl" style={{ background: isLight ? "rgba(110,70,214,0.16)" : "rgba(110,70,214,0.20)" }} />
        <div className="absolute left-[18%] bottom-[18%] h-16 w-16 rounded-full border-2" style={{ borderColor: isLight ? "rgba(183,121,0,0.32)" : "rgba(252,207,31,0.30)" }} />
        <div className="absolute left-[22%] bottom-[20%] h-16 w-16 rounded-full border-2" style={{ borderColor: isLight ? "rgba(110,70,214,0.20)" : "rgba(255,255,255,0.18)" }} />
        <div className="absolute right-[10%] bottom-[14%] flex gap-3 opacity-80">
          {[0, 1, 2].map((i) => (
            <span key={i} className="block h-3 w-3 rounded-full" style={{ backgroundColor: isLight ? "#b77900" : "#fccf1f" }} />
          ))}
        </div>
        <div className="absolute right-[16%] bottom-[24%] h-24 w-40 rounded-[1.75rem] border" style={{ borderColor: isLight ? "rgba(110,70,214,0.20)" : "rgba(255,255,255,0.16)", transform: "rotate(-10deg)" }} />
        <span
          className="absolute left-[14%] top-[18%] font-nunito font-black text-8xl leading-none"
          style={{ color: isLight ? "rgba(183,121,0,0.18)" : "rgba(252,207,31,0.22)", transform: "rotate(-10deg)" }}
        >
          ?
        </span>
        <span
          className="absolute right-[18%] top-[20%] font-nunito font-black text-7xl leading-none"
          style={{ color: isLight ? "rgba(110,70,214,0.16)" : "rgba(255,255,255,0.18)", transform: "rotate(8deg)" }}
        >
          !
        </span>
        <span
          className="absolute left-[32%] bottom-[14%] font-nunito font-black text-5xl leading-none"
          style={{ color: isLight ? "rgba(110,70,214,0.14)" : "rgba(252,207,31,0.16)" }}
        >
          ?
        </span>
      </>
    );
  }

  if (variant === "futuristic-playful") {
    return (
      <>
        <div className="absolute inset-x-0 top-0 h-44" style={{ background: isLight ? "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(245,248,255,0.28) 100%)" : "linear-gradient(180deg, rgba(7,10,20,0.92) 0%, rgba(7,10,20,0.14) 100%)" }} />
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: isLight ? "linear-gradient(rgba(95,99,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(95,99,255,0.08) 1px, transparent 1px)" : "linear-gradient(rgba(95,99,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(95,99,255,0.10) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="absolute left-[8%] top-[22%] h-56 w-56 rounded-full blur-3xl" style={{ background: isLight ? "rgba(95,99,255,0.12)" : "rgba(95,99,255,0.16)" }} />
        <div className="absolute right-[10%] top-[18%] h-48 w-48 rounded-full blur-3xl" style={{ background: isLight ? "rgba(110,70,214,0.12)" : "rgba(110,70,214,0.16)" }} />
        <div className="absolute left-[14%] bottom-[18%] right-[14%] flex items-center justify-between opacity-80">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="block h-3 w-3 rounded-full" style={{ backgroundColor: isLight ? "#5f63ff" : "#8b8dff" }} />
              {i < 3 && <span className="block h-[2px] w-14" style={{ backgroundColor: isLight ? "rgba(95,99,255,0.28)" : "rgba(95,99,255,0.22)" }} />}
            </div>
          ))}
        </div>
        <span
          className="absolute left-[12%] top-[18%] font-nunito font-black text-7xl leading-none"
          style={{ color: isLight ? "rgba(95,99,255,0.18)" : "rgba(139,141,255,0.20)" }}
        >
          ?
        </span>
        <span
          className="absolute right-[12%] bottom-[24%] font-nunito font-black text-6xl leading-none"
          style={{ color: isLight ? "rgba(110,70,214,0.16)" : "rgba(255,255,255,0.16)" }}
        >
          !
        </span>
      </>
    );
  }

  return null;
}

export function HeroSection({ content, style, pageSlug }) {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const variant = getHeroVariant(pageSlug);
  const palette = getHeroPalette(variant, isLight);
  const alignment = content.alignment || "center";
  const alignClass = {
    left: "text-left items-start",
    center: "text-center items-center",
    right: "text-right items-end",
  }[alignment];

  return (
    <section
      className="relative flex flex-col justify-center overflow-hidden"
      style={{
        ...style,
        minHeight: content.minHeight || "80vh",
        background: variant !== "default" ? palette.base : undefined,
        backgroundColor:
          variant === "default"
            ? normalizeSurfaceColor(style?.backgroundColor, isLight, "var(--lotq-light-purple-panel)")
            : undefined,
        backgroundImage:
          variant === "default" && content.backgroundImage ? `url(${content.backgroundImage})` : undefined,
        backgroundSize: variant === "default" ? "cover" : undefined,
        backgroundPosition: variant === "default" ? "center" : undefined,
      }}
    >
      {variant !== "default" && <HeroArtwork variant={variant} isLight={isLight} />}

      {(content.backgroundImage || variant !== "default") && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: variant === "default" ? normalizeOverlayColor(content.overlay, isLight) : palette.overlay }}
        />
      )}

      <div className={`relative z-10 container mx-auto px-6 flex flex-col ${alignClass}`}>
        {content.logoImage ? (
          <div className="mb-6 flex justify-center">
            <img
              src={content.logoImage}
              alt={content.title || "Logo"}
              className="w-full max-w-[500px] md:max-w-[600px] lg:max-w-[700px] h-auto object-contain"
              style={{
                filter: isLight
                  ? "brightness(0) saturate(100%) drop-shadow(0 0 18px rgba(110,70,214,0.10))"
                  : "drop-shadow(0 0 15px rgba(252,207,31,0.3))",
              }}
            />
          </div>
        ) : (
          <h1
            className="font-nunito font-black text-4xl sm:text-5xl lg:text-7xl uppercase tracking-wider mb-6"
            style={{ color: palette.title, ...content.titleStyle }}
          >
            {content.title || "Título Principal"}
          </h1>
        )}

        {content.subtitle && (
          <p className="text-xl md:text-2xl mb-4 max-w-2xl" style={{ color: palette.subtitle, ...content.subtitleStyle }}>
            {content.subtitle}
          </p>
        )}

        {content.description && (
          <p className="text-lg mb-8 max-w-xl" style={{ color: palette.description }}>
            {content.description}
          </p>
        )}

        {content.buttons && content.buttons.length > 0 && (
          <div className="flex flex-wrap gap-4 justify-center">
            {content.buttons.map((btn, index) => (
              <Link key={index} to={btn.link || "/"}>
                <Button
                  className={
                    btn.style === "primary"
                      ? palette.primaryButton
                      : btn.style === "outline"
                        ? palette.outlineButton
                        : palette.ghostButton
                  }
                >
                  {btn.text}
                  {btn.style === "primary" && <ChevronRight className="ml-2 w-5 h-5" />}
                </Button>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const CAROUSEL_ACCENTS = {
  gold: {
    glow: "rgba(252,207,31,0.30)",
    ring: "rgba(252,207,31,0.38)",
    button: "bg-[#fccf1f] text-black hover:bg-[#e6bc1c]",
    label: "text-[#fccf1f]",
  },
  purple: {
    glow: "rgba(168,85,247,0.32)",
    ring: "rgba(196,132,252,0.38)",
    button: "bg-purple-400 text-purple-950 hover:bg-purple-300",
    label: "text-purple-300",
  },
  blue: {
    glow: "rgba(56,189,248,0.30)",
    ring: "rgba(125,211,252,0.38)",
    button: "bg-sky-300 text-sky-950 hover:bg-sky-200",
    label: "text-sky-300",
  },
  green: {
    glow: "rgba(52,211,153,0.30)",
    ring: "rgba(110,231,183,0.38)",
    button: "bg-emerald-300 text-emerald-950 hover:bg-emerald-200",
    label: "text-emerald-300",
  },
};

function isExternalLink(link) {
  return /^https?:\/\//i.test(String(link || ""));
}

export function CampaignCarousel({ content = {}, style = {}, className = "" }) {
  const slides = (content.slides || []).filter((slide) => slide?.title);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = slides[activeIndex] || slides[0];
  const accent = CAROUSEL_ACCENTS[activeSlide?.accent] || CAROUSEL_ACCENTS.gold;
  const intervalMs = Math.max(3, Math.min(30, Number(content.intervalSeconds) || 7)) * 1000;

  useEffect(() => {
    setActiveIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (!content.autoPlay || slides.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [content.autoPlay, intervalMs, slides.length]);

  if (!activeSlide) return null;

  const move = (direction) => {
    setActiveIndex((current) => (current + direction + slides.length) % slides.length);
  };
  const cta = activeSlide.ctaLabel && activeSlide.ctaLink ? (
    isExternalLink(activeSlide.ctaLink) ? (
      <a href={activeSlide.ctaLink} target="_blank" rel="noreferrer" className={`inline-flex items-center rounded-xl px-5 py-3 font-nunito font-black transition ${accent.button}`}>
        {activeSlide.ctaLabel}<ChevronRight className="ml-2 h-4 w-4" />
      </a>
    ) : (
      <Link to={activeSlide.ctaLink} className={`inline-flex items-center rounded-xl px-5 py-3 font-nunito font-black transition ${accent.button}`}>
        {activeSlide.ctaLabel}<ChevronRight className="ml-2 h-4 w-4" />
      </Link>
    )
  ) : null;

  return (
    <section
      className={`lotq-photo-surface ${content.fullBleed ? "w-full" : "px-4 md:px-6"} ${className}`}
      style={{ ...style, paddingTop: style?.paddingTop || undefined, paddingBottom: style?.paddingBottom || undefined }}
      aria-label={content.title || "Destaques"}
    >
      <div className={`${content.fullBleed ? "mx-0 rounded-none md:rounded-[32px]" : "mx-auto max-w-6xl rounded-[28px]"} relative isolate min-h-[310px] overflow-hidden border border-white/10 bg-zinc-950 shadow-[0_22px_70px_rgba(0,0,0,0.30)] md:min-h-[340px]`}>
        {activeSlide.image?.url ? (
          <img src={activeSlide.image.url} alt={activeSlide.image.alt || activeSlide.title} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <>
            <div className="absolute inset-0 bg-[linear-gradient(120deg,#121015_0%,#17151f_42%,#09090b_100%)]" />
            <div className="absolute -right-16 -top-24 h-80 w-80 rounded-full blur-3xl" style={{ background: accent.glow }} />
            <div className="absolute bottom-[-9rem] right-[18%] h-72 w-72 rounded-full border-2" style={{ borderColor: accent.ring }} />
            <div className="absolute right-[7%] top-[18%] h-24 w-24 rounded-3xl border border-white/10" style={{ transform: "rotate(18deg)", background: `${accent.glow}` }} />
            <div className="absolute bottom-[18%] right-[12%] font-nunito text-8xl font-black text-white/[0.07]">?</div>
          </>
        )}
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,0.94)_0%,rgba(5,5,5,0.80)_45%,rgba(5,5,5,0.24)_100%)]" />
        <div className="relative z-10 flex min-h-[310px] max-w-3xl flex-col justify-center px-6 py-9 sm:px-10 md:min-h-[340px] md:px-14">
          {activeSlide.eyebrow && <p className={`font-outfit text-xs font-bold uppercase tracking-[0.24em] ${accent.label}`}>{activeSlide.eyebrow}</p>}
          <h2 className="mt-3 max-w-2xl font-nunito text-3xl font-black tracking-tight text-white sm:text-4xl md:text-5xl">{activeSlide.title}</h2>
          {activeSlide.description && <p className="mt-4 max-w-xl font-outfit text-sm leading-relaxed text-zinc-300 sm:text-base">{activeSlide.description}</p>}
          {cta && <div className="mt-7">{cta}</div>}
        </div>
        {slides.length > 1 && (
          <>
            <div className="absolute bottom-5 left-6 z-20 flex gap-2 sm:left-10 md:left-14">
              {slides.map((slide, index) => <button key={`${slide.title}-${index}`} type="button" aria-label={`Exibir slide ${index + 1}`} onClick={() => setActiveIndex(index)} className={`h-2 rounded-full transition-all ${index === activeIndex ? "w-7 bg-white" : "w-2 bg-white/35 hover:bg-white/70"}`} />)}
            </div>
            <div className="absolute bottom-5 right-5 z-20 flex gap-2">
              <button type="button" onClick={() => move(-1)} aria-label="Slide anterior" className="rounded-full border border-white/15 bg-black/35 p-2 text-white transition hover:bg-white/15"><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(1)} aria-label="Próximo slide" className="rounded-full border border-white/15 bg-black/35 p-2 text-white transition hover:bg-white/15"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export function CarouselSection({ content, style }) {
  return <CampaignCarousel content={content} style={style} className="py-8 md:py-12" />;
}

export function TextSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "1200px" }}>
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] mb-8" style={content.titleStyle}>
            {content.title}
          </h2>
        )}

        <div
          className={`prose prose-invert max-w-none ${content.columns > 1 ? `columns-${content.columns} gap-8` : ""}`}
          style={content.contentStyle}
        >
          {content.content?.split("\n").map((paragraph, index) => (
            <p key={index} className="text-white/80 text-lg leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CardsSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");
  const columns = content.columns || 3;

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "1200px" }}>
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-4">
            {content.title}
          </h2>
        )}

        {content.subtitle && (
          <p className="text-white/60 text-center text-lg mb-12 max-w-2xl mx-auto">{content.subtitle}</p>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-6`}>
          {content.cards?.map((card, index) => (
            <div
              key={index}
              className={`p-6 rounded-xl transition-all duration-300 hover:scale-105 ${
                content.cardStyle === "bordered"
                  ? "border border-white/10 hover:border-[#fccf1f]/50"
                  : content.cardStyle === "elevated"
                    ? "bg-white/5 shadow-xl hover:shadow-2xl"
                    : "bg-gradient-to-br from-white/5 to-transparent border border-white/5"
              }`}
            >
              {card.icon && <div className="text-4xl mb-4">{card.icon}</div>}

              {card.image?.url && (
                <img
                  src={card.image.url}
                  alt={card.image.alt || card.title}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}

              <h3 className="font-nunito font-bold text-xl text-[#fccf1f] mb-2">{card.title}</h3>

              {card.description && <p className="text-white/70">{card.description}</p>}

              {card.link && (
                <Link to={card.link} className="inline-block mt-4 text-[#fccf1f] hover:underline">
                  Saiba mais →
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection({ content, style }) {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const sectionBackground = content.backgroundColor || style?.backgroundColor || "#fccf1f";

  return (
    <section
      className="py-20 px-6 relative overflow-hidden"
      style={{
        ...style,
        backgroundColor: normalizeSurfaceColor(sectionBackground, isLight, "var(--lotq-light-purple-panel-strong)"),
        backgroundImage: content.backgroundImage ? `url(${content.backgroundImage})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {content.backgroundImage && (
        <div className="absolute inset-0" style={{ backgroundColor: normalizeOverlayColor("rgba(0,0,0,0.5)", isLight) }} />
      )}

      <div className="container mx-auto text-center relative z-10">
        <h2
          className={`font-nunito font-bold text-3xl md:text-5xl mb-6 ${
            sectionBackground === "#fccf1f" || (!content.backgroundImage && !content.backgroundColor && !isLight)
              ? "text-black"
              : isLight ? "text-black" : "text-[#fccf1f]"
          }`}
        >
          {content.title || "Pronto para começar?"}
        </h2>

        {content.description && (
          <p
            className={`text-xl mb-8 max-w-2xl mx-auto ${
              sectionBackground === "#fccf1f" || (!content.backgroundImage && !content.backgroundColor && !isLight)
                ? "text-black/70"
                : isLight ? "text-black/70" : "text-white/80"
            }`}
          >
            {content.description}
          </p>
        )}

        {content.buttons && content.buttons.length > 0 && (
          <div className="flex flex-wrap gap-4 justify-center">
            {content.buttons.map((btn, index) => (
              <Link key={index} to={btn.link || "/"}>
                <Button
                  className={
                    sectionBackground === "#fccf1f" || (!content.backgroundImage && !content.backgroundColor && !isLight)
                      ? "bg-black text-[#fccf1f] hover:bg-black/80 font-bold px-8 py-6 text-lg"
                      : "bg-[#fccf1f] text-black hover:bg-[#fccf1f]/90 font-bold px-8 py-6 text-lg"
                  }
                >
                  {btn.text}
                </Button>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function FAQSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");
  const [openIndex, setOpenIndex] = React.useState(null);

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "800px" }}>
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-12">
            {content.title}
          </h2>
        )}

        <div className="space-y-4">
          {content.items?.map((item, index) => (
            <div key={index} className="border border-white/10 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
              >
                <span className="font-semibold text-white">{item.question}</span>
                <ChevronRight
                  className={`w-5 h-5 text-[#fccf1f] transition-transform ${openIndex === index ? "rotate-90" : ""}`}
                />
              </button>

              {openIndex === index && <div className="px-6 pb-4 text-white/70">{item.answer}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeaturesSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");
  const columns = content.columns || 3;

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "1200px" }}>
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-4">
            {content.title}
          </h2>
        )}

        {content.subtitle && (
          <p className="text-white/60 text-center text-lg mb-12 max-w-2xl mx-auto">{content.subtitle}</p>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-8`}>
          {content.features?.map((feature, index) => (
            <div key={index} className={`${content.layout === "alternating" && index % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
              {feature.icon && <div className="text-5xl mb-4 text-[#fccf1f]">{feature.icon}</div>}

              {feature.image && (
                <img src={feature.image} alt={feature.title} className="w-full h-48 object-cover rounded-lg mb-4" />
              )}

              <h3 className="font-nunito font-bold text-xl text-white mb-2">{feature.title}</h3>
              <p className="text-white/70">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ImageSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");
  const image = content.image || {};
  const ImageWrapper = content.link ? Link : "div";
  const wrapperProps = content.link ? { to: content.link } : {};

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "1200px" }}>
        <ImageWrapper {...wrapperProps} className="block">
          <img
            src={image.url}
            alt={image.alt || ""}
            className="w-full rounded-lg"
            style={{
              maxWidth: image.width || "100%",
              height: image.height || "auto",
              objectFit: image.objectFit || "cover",
              margin: "0 auto",
            }}
          />
        </ImageWrapper>

        {content.caption && <p className="text-center text-white/60 mt-4 text-sm">{content.caption}</p>}
      </div>
    </section>
  );
}

export function GallerySection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");
  const columns = content.columns || 3;

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "1200px" }}>
        <div className={`grid grid-cols-2 md:grid-cols-${columns}`} style={{ gap: content.gap || "16px" }}>
          {content.images?.map((image, index) => (
            <div key={index} className="relative group overflow-hidden rounded-lg">
              <img
                src={image.url}
                alt={image.alt || ""}
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />

              {content.showCaptions && image.alt && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <p className="text-white text-sm">{image.alt}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style, "transparent");
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const testimonials = content.testimonials || [];

  return (
    <section className="py-16 px-6" style={sectionStyle}>
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "1000px" }}>
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-12">
            {content.title}
          </h2>
        )}

        {content.layout === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        ) : (
          <div className="relative">
            {testimonials.length > 0 && <TestimonialCard testimonial={testimonials[currentIndex]} />}

            {testimonials.length > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${index === currentIndex ? "bg-[#fccf1f]" : "bg-white/30"}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center gap-4 mb-4">
        {testimonial.avatar ? (
          <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#fccf1f]/20 flex items-center justify-center">
            <span className="text-[#fccf1f] font-bold text-lg">{testimonial.name?.charAt(0) || "?"}</span>
          </div>
        )}

        <div>
          <h4 className="font-bold text-white">{testimonial.name}</h4>
          {testimonial.role && <p className="text-white/60 text-sm">{testimonial.role}</p>}
        </div>
      </div>

      {testimonial.rating && (
        <div className="flex gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <span key={i} className={i < testimonial.rating ? "text-[#fccf1f]" : "text-white/20"}>
              ★
            </span>
          ))}
        </div>
      )}

      <p className="text-white/80 italic">"{testimonial.content}"</p>
    </div>
  );
}

export function SpacerSection({ content, style }) {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  return (
    <div
      style={{
        height: content.height || "40px",
        backgroundColor: normalizeSurfaceColor(style?.backgroundColor, isLight, "transparent"),
      }}
    />
  );
}

export function CustomHTMLSection({ content, style }) {
  const { sectionStyle } = useThemeAwareBackground(style);
  return (
    <section className="py-8 px-6" style={sectionStyle}>
      <div className="container mx-auto">
        {content.css && <style dangerouslySetInnerHTML={{ __html: content.css }} />}
        <div dangerouslySetInnerHTML={{ __html: content.html || "" }} />
      </div>
    </section>
  );
}

export function ContactFormSection({ content, style }) {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const [formData, setFormData] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      const requestData = {
        type: formData.type || content.defaultType || "corporate",
        name: formData.name || "",
        email: formData.email || "",
        phone: formData.phone || "",
        event_date: formData.event_date || formData.date || new Date().toISOString().split("T")[0],
        num_people: parseInt(formData.num_people || formData.people || "10"),
        message: formData.message || "",
      };

      const response = await fetch(`${API}/booking-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error("Erro ao enviar solicitação");
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError("Erro ao enviar. Por favor, tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const fields = content.fields || ["name", "email", "phone", "num_people", "message"];
  const fieldLabels = {
    name: "Nome",
    email: "Email",
    phone: "Telefone",
    num_people: "Número de Pessoas/Lotação",
    people: "Número de Pessoas/Lotação",
    event_date: "Data do Evento",
    date: "Data do Evento",
    subject: "Assunto",
    message: "Mensagem",
    type: "Tipo de Evento",
  };

  const fieldTypes = {
    email: "email",
    num_people: "number",
    people: "number",
    event_date: "date",
    date: "date",
  };

  if (submitted) {
    return (
      <section
        className="py-16 px-6"
        style={{
          backgroundColor: normalizeSurfaceColor(style?.backgroundColor, isLight, "transparent"),
          ...style,
        }}
      >
        <div className="container mx-auto text-center" style={{ maxWidth: "600px" }}>
          <div className="text-6xl mb-4">✓</div>
          <h3 className="text-2xl font-bold text-[#fccf1f] mb-2">Solicitação Enviada!</h3>
          <p className="text-white/70">Entraremos em contato em breve.</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 px-6"
      style={{
        backgroundColor: normalizeSurfaceColor(style?.backgroundColor, isLight, "transparent"),
        ...style,
      }}
    >
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || "600px" }}>
        {content.title && (
          <h2 id="contato" className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-4">
            {content.title}
          </h2>
        )}

        {content.description && <p className="text-white/60 text-center text-lg mb-8">{content.description}</p>}

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {fields.map((field) => (
            <div key={field}>
              <label className="block text-white mb-2">{fieldLabels[field] || field}</label>
              {field === "message" ? (
                <textarea
                  value={formData[field] || ""}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#fccf1f] focus:outline-none resize-none"
                  rows={5}
                  required
                />
              ) : field === "type" ? (
                <select
                  value={formData[field] || ""}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-900 border border-white/10 rounded-lg text-white focus:border-[#fccf1f] focus:outline-none"
                  required
                  style={{ backgroundColor: isLight ? "#ffffff" : "#18181b" }}
                >
                  <option value="" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#475569" : "#a1a1aa" }}>Selecione...</option>
                  <option value="corporate" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#111827" : "#ffffff" }}>Evento Corporativo</option>
                  <option value="convention" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#111827" : "#ffffff" }}>Feira / Convenção</option>
                  <option value="private" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#111827" : "#ffffff" }}>Festa Privada</option>
                  <option value="public" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#111827" : "#ffffff" }}>Evento Público</option>
                  <option value="recurring" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#111827" : "#ffffff" }}>Evento recorrente em bar/restaurante</option>
                  <option value="other" style={{ backgroundColor: isLight ? "#ffffff" : "#18181b", color: isLight ? "#111827" : "#ffffff" }}>Outros</option>
                </select>
              ) : (
                <input
                  type={fieldTypes[field] || "text"}
                  value={formData[field] || ""}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#fccf1f] focus:outline-none"
                  required
                />
              )}
            </div>
          ))}

          <Button type="submit" disabled={submitting} className="w-full bg-[#fccf1f] text-black hover:bg-[#fccf1f]/90 font-bold py-4 text-lg">
            {submitting ? "Enviando..." : content.submitText || "Enviar Solicitação"}
          </Button>
        </form>
      </div>
    </section>
  );
}
